import { FC, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ViewContainer } from "../../../../shared/components/viewContainer";
import { TitleBarProps } from "../../../../shared/components/titleBar";
import { Box, Grid, Tab, Tabs } from "@mui/material";
import SingleEntityContainer from "../../../../shared/components/singleEntityContainer";
import { useFormik } from "formik";
import InputTextfield from "../../../../shared/components/textField";
import { FormContainer } from "../../../../shared/components/formContainer";
import useQuery from "../../../../shared/components/hooks/useQuery";
import { Reseller } from "../../../../shared/interfaces/reseller";
import { getTextFields } from "../variables/textFields";
import TabPanel from "../../../../shared/components/tabpanel/tabpanel";
import ActiveService from "./activeService";
import { customizeValidationSchema } from "../variables/customSchema";
import useMutation from "../../../../shared/components/hooks/useMutation";
import AddressList from "./addressList/addressList";
import ContactList from "./contactList/contactList";
import { EditButton } from "../../../../shared/components/editButton";
import { CloseButton } from "../../../../shared/components/closeButton";
import { ProfileBorderBox } from "../../../../shared/components/profileComponents";
import { styled } from "@mui/material/styles";
import DomainList from "./domainList";

const TabContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  paddingLeft: "1rem",
  marginLeft: "1.8em",
  marginRight: "2.8em",
}));

const ResellerEditView: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const organizationId = id ?? "";
  const [editStatus, setEditStatus] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const { loading, data, fetchData, error } = useQuery(
    `/admin/organization/${id}`,
    null
  );
  const { loading: apiLoading, modifyData } = useMutation();
  const resellerData: Reseller = (data as any)?.data || {};
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const initialValues = {
    name: resellerData?.name ?? "",
    phone: resellerData?.organizationProfile?.phone ?? "",
    email: resellerData?.organizationProfile?.email ?? "",
    subdomainName: resellerData?.subdomainName ?? "",
    // domainName: resellerData?.domainName ?? "",
  };

  const resellerForm = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validateOnBlur: true,
    validationSchema: customizeValidationSchema(),
    onSubmit: async (formData: any) => {
      let response;
      if (id) {
        response = await modifyData(
          `/admin/organization/${id}`,
          "put",
          {
            ...formData,
          },
          resellerForm
        );
      }

      if (response) {
        fetchData();
        closeEdit();
      }
    },
  });

  const closeEdit = () => {
    fetchData();
    setEditStatus(false);
  };

  const viewTitleBar: TitleBarProps = {
    title: "Reseller Details",
    headerTitle: "Reseller Details",
  };
  const editTitlebar: TitleBarProps = {
    headerTitle: "Reseller - Edit",
    title: "Edit Reseller",
    buttonTitle: "Save",
    onClick: () => resellerForm.handleSubmit(),
    dirty: resellerForm.dirty,
    buttonLoading: apiLoading,
    editCloseIcon: {
      visible: true,
      onClick: () => closeEdit(),
    },
  };

  const resellerFields = getTextFields(resellerData);

  const closeDetailsView = () => {
    navigate("/resellers");
  };

  const entityPermissions = (data as any)?.entityPermissions
  const editPermission = (data as any)?.entityPermissions
    ? (data as any)?.entityPermissions?.includes("Reseller-Update")
    : true;

  const resellerEditTooltipNode = (
    <>
      {editPermission && <EditButton onClick={() => setEditStatus(true)} />}
      <CloseButton onClick={() => closeDetailsView()} />
    </>
  );

  return (
    <ProfileBorderBox>
      {editStatus && (
        <FormContainer titleBar={editTitlebar}>
          <Box className="form-border-custom">
            <Grid
              container
              alignItems="stretch"
              columnSpacing={4}
              rowSpacing={2}
              marginTop={2}
            >
              {resellerFields.map(
                (field: any, index: number) =>
                  (field?.formField === undefined ||
                    field.formField === true) && (
                    <Grid item md={6} sm={6} xs={12} key={field.id}>
                      <InputTextfield
                        form={resellerForm}
                        fieldProps={field}
                        key={index}
                        disabled={field?.disabled}
                      />
                    </Grid>
                  )
              )}
            </Grid>
          </Box>
        </FormContainer>
      )}

      {!editStatus && !error && (
        <>
          <div className="viewContainerResellerHeight">
            <ViewContainer loading={loading} titleBar={viewTitleBar}>
              <div className="entity-container">
                <SingleEntityContainer
                  entityFields={resellerFields}
                  tooltips={resellerEditTooltipNode}
                ></SingleEntityContainer>
              </div>
            </ViewContainer>
          </div>
          <ViewContainer
            tabs={
              <TabContainer>
                <Tabs value={activeTab} onChange={handleTabChange}>
                  <Tab label="Services" />
                  <Tab label="Addresses" />
                  <Tab label="Contacts" />
                  <Tab label="Domain Configuration" />
                </Tabs>
              </TabContainer>
            }
          >
            <div className="tab-panel">
              <TabPanel value={activeTab} index={0}>
                {!loading && <ActiveService organizationId={organizationId} entityPermissions={entityPermissions} />}
              </TabPanel>
              <TabPanel value={activeTab} index={1}>
                <AddressList organizationId={organizationId} entityPermissions={entityPermissions}/>
              </TabPanel>
              <TabPanel value={activeTab} index={2}>
                <ContactList organizationId={organizationId} entityPermissions={entityPermissions} />
              </TabPanel>
              <TabPanel value={activeTab} index={3}>
                <DomainList
                  organizationId={organizationId}
                  domainName={resellerData?.domainName}
                  entityPermissions={entityPermissions}
                  fetchData={fetchData}
                />
              </TabPanel>
            </div>
          </ViewContainer>
        </>
      )}
    </ProfileBorderBox>
  );
};

export default ResellerEditView;
